import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Select,
  Button,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  FormHelperText,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { addTags, updateTags } from 'src/services/tag.service';

import { scrollbarStyle } from 'src/sections/style';

function AddUpdateTags({
  hideModal,
  handleFetchTags,
  row,
  departmentList,
  initialData,
  canUpdate = true,
}) {
  const { showToast, hideToast } = useToast();
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const [loading, setLoading] = useState(false);

  const { id, departmentIds } = useAuth();

  const isPrefilled = !!initialData;

  const isEditable = !row || canUpdate;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'all',
    defaultValues: {
      name: row?.name || initialData?.name || '',
      description: row?.description || '',
      department:
        row?.department?.id || initialData?.departmentId || departmentIds?.[0] || '',
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        description: data.description,
        department: {
          id: data.department || initialData?.departmentId || departmentIds[0],
        },
        createdBy: { id },
      };

      let result;
      if (row) {
        result = await updateTags(row.id, payload);
        showToast({
          message: t('successMessages.tagUpdatedSuccessfully'),
          severity: 'success',
          props: { hideToast },
        });
      } else {
        result = await addTags(payload);
        showToast({
          message: t('successMessages.tagAddedSuccessfully'),
          severity: 'success',
          props: { hideToast },
        });
      }

      if (handleFetchTags) handleFetchTags(result);
      hideModal();
    } catch (error) {
      if (error.response?.data === 'name exists') {
        showToast({
          message: t('warningMessages.tagNameExists'),
          severity: 'warning',
          props: { hideToast },
        });
      } else {
        showToast({
          message: row
            ? t('errorMessages.errorUpdatingTag')
            : t('errorMessages.errorAddingTag'),
          severity: 'error',
          props: { hideToast },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    reset({
      name: row?.name || initialData?.name || '',
      description: row?.description || '',
      department: row?.department?.id || initialData?.departmentId || '',
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2.5} mt={1}>
        <TextField
          {...register('name', { required: true })}
          label={t('pages.tagsPage.name')}
          fullWidth
          error={!!errors.name}
          helperText={errors.name && t('formControl.fieldRequired')}
          size={isSmallScreen ? 'small' : 'medium'}
          // Removed (isPrefilled && !row) so the pre-filled name remains fully editable during creation
          disabled={!isEditable}
        />

        <TextField
          multiline
          minRows={4}
          {...register('description')}
          label={t('pages.tagsPage.description')}
          error={!!errors.description}
          size={isSmallScreen ? 'small' : 'medium'}
          disabled={!isEditable}
        />

        <FormControl
          fullWidth
          error={!!errors.department}
          size={isSmallScreen ? 'small' : 'medium'}
          disabled={!!row || isPrefilled || !isEditable}
        >
          <InputLabel id="department-select-label">
            {t('pages.tagsPage.department')}
          </InputLabel>

          <Select
            labelId="department-select-label"
            label={t('pages.tagsPage.department')}
            value={watch('department')}
            {...register('department', { required: true })}
            MenuProps={{ PaperProps: { sx: scrollbarStyle } }}
          >
            {/* If departmentList is provided, map it. Otherwise show the pre-filled ID */}
            {departmentList?.length > 0 ? (
              departmentList.map((dep) => (
                <MenuItem key={dep.id} value={dep.id}>
                  {dep.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={watch('department')}>
                {initialData?.departmentName || t('attributes.department')}
              </MenuItem>
            )}
          </Select>
          <FormHelperText>{errors.department?.message}</FormHelperText>
        </FormControl>

        {!isEditable ? (
          <Stack direction="row" mt={2}>
            <Button fullWidth variant="contained" color="inherit" onClick={hideModal}>
              {t('buttons.close') || 'Close'}
            </Button>
          </Stack>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            {row && (
              <LoadingButton
                fullWidth
                variant="outlined"
                color="error"
                disabled={!isDirty}
                onClick={handleReset}
              >
                {t('buttons.cancelChanges')}
              </LoadingButton>
            )}
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              disabled={!isValid || (!isDirty && !isPrefilled)}
              loading={loading}
            >
              {row ? t('buttons.editTag') : t('buttons.addTag')}
            </LoadingButton>
          </Stack>
        )}
      </Stack>
    </form>
  );
}

AddUpdateTags.propTypes = {
  hideModal: PropTypes.func,
  handleFetchTags: PropTypes.func,
  row: PropTypes.object,
  departmentList: PropTypes.array,
  initialData: PropTypes.object,
  canUpdate: PropTypes.bool,
};

export default AddUpdateTags;
